import {
  Button,
  Flex,
  FormControl,
  Radio,
  RadioGroup,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import PedidoBox from '../../../../../components/shared/Certificados/PedidoBox';

const FormSchema = z.object({
  tipoPedido: z.enum(['1', '2', '3']),
  finalidade: z.enum(['1', '2', '3', '4']),
  tipoCertificado: z.enum(['1', '2', '3', '4', '5', '6']),
  duracao: z.enum(['1', '2', '3', '4', '5', '6']),
});

export type stepPedidoData = z.infer<typeof FormSchema>;

interface IPedido {
  pedidoData: stepPedidoData;
  setPedidoData(data: any): any;
  handleMudaStep(novoStep: number): void;
}

const Pedido: React.FC<IPedido> = ({
  pedidoData,
  setPedidoData,
  handleMudaStep,
}) => {
  const Toast = useToast();
  const color = useColorModeValue('gray.800', 'white');

  const methods = useForm<stepPedidoData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      tipoPedido: pedidoData.tipoPedido || '2',
      finalidade: pedidoData.finalidade || '1',
      tipoCertificado: pedidoData.tipoCertificado || '1',
      duracao: pedidoData.duracao || '1',
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    register,
    setValue,
    control,
    formState: { errors },
  } = methods;

  // watchs
  const finalidade = watch('finalidade');
  const tipoCertificado = watch('tipoCertificado');

  useEffect(() => {
    if (finalidade === '4') {
      setValue('tipoCertificado', '6');
    } else {
      setValue('tipoCertificado', '1');
    }
  }, [finalidade]);

  useEffect(() => {
    setValue('duracao', '');
  }, [tipoCertificado]);

  useEffect(() => {
    setValue('duracao', '1');
  }, []);

  const handleSalvaPedido = async (data: stepPedidoData) => {
    try {
      setPedidoData(data);
      handleMudaStep(2);
    } catch (error) {
      //
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(handleSalvaPedido, errors => {
          Toast({
            title: 'Validação',
            description: 'Preencha os campos destacados.',
            status: 'error',
            duration: 4000,
            position: 'bottom-right',
            isClosable: true,
          });
        })}
      >
        <Flex justify="space-between" mx="8">
          <Text />
          <Button type="submit">Avançar</Button>
        </Flex>
        <Flex w="100%">
          <SimpleGrid
            columns={[1, 1, 4, 4]}
            spacing="20px"
            my="4"
            mx="4"
            w="100%"
          >
            <PedidoBox titulo="Pedido">
              <Controller
                control={control}
                name="tipoPedido"
                render={({ field }) => (
                  <FormControl>
                    <RadioGroup {...field}>
                      <VStack align="flex-start" p="2">
                        <Radio value="1" size="lg">
                          <Text color={color}>Presencial</Text>
                        </Radio>
                        <Radio value="2" size="lg">
                          <Text color={color}>Vídeoconferência</Text>
                        </Radio>
                        <Radio value="3" size="lg">
                          <Text color={color}>Emissão automática</Text>
                        </Radio>
                      </VStack>
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </PedidoBox>
            <PedidoBox titulo="Finalidade">
              <Controller
                control={control}
                name="finalidade"
                render={({ field }) => (
                  <FormControl>
                    <RadioGroup {...field}>
                      <VStack align="flex-start" p="2">
                        <Radio value="1" size="lg">
                          <Text color={color}>Pessoa Juridica</Text>
                        </Radio>
                        <Radio value="2" size="lg">
                          <Text color={color}>Pessoa Fisica</Text>
                        </Radio>
                        <Radio value="3" size="lg">
                          <Text color={color}>NF-e</Text>
                        </Radio>
                        <Radio value="4" size="lg">
                          <Text color={color}>CPF Bird ID (Nuvem)</Text>
                        </Radio>
                      </VStack>
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </PedidoBox>
            <PedidoBox titulo="Tipo Certificado" dependencia={finalidade}>
              <Controller
                control={control}
                name="tipoCertificado"
                render={({ field }) => (
                  <FormControl>
                    <RadioGroup {...field}>
                      <VStack align="flex-start" p="2">
                        {finalidade !== '4' ? (
                          <>
                            <Radio value="1" size="lg">
                              <Text color={color}>Arquivo Digital (A1)</Text>
                            </Radio>
                            <Radio value="2" size="lg">
                              <Text color={color}>Token (A3)</Text>
                            </Radio>
                            <Radio value="3" size="lg">
                              <Text color={color}>Cartão(A3) sem leitora</Text>
                            </Radio>
                            <Radio value="4" size="lg">
                              <Text color={color}>Cartão(A3) + leitora</Text>
                            </Radio>
                            <Radio value="5" size="lg">
                              <Text color={color}>A3 sem mídia</Text>
                            </Radio>
                          </>
                        ) : (
                          <Radio value="6" size="lg">
                            <Text color={color}>CPF A3 (Nuvem)</Text>
                          </Radio>
                        )}
                      </VStack>
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </PedidoBox>
            <PedidoBox
              titulo="Duração"
              dependencia={tipoCertificado}
              error={errors?.duracao}
            >
              <Controller
                control={control}
                name="duracao"
                render={({ field }) => (
                  <FormControl>
                    <RadioGroup {...field}>
                      <VStack align="flex-start" p="2">
                        {tipoCertificado < '6' && (
                          <>
                            <Radio value="1" size="lg">
                              <Text color={color}>12 Meses</Text>
                            </Radio>
                          </>
                        )}
                        {tipoCertificado < '6' && tipoCertificado > '1' && (
                          <>
                            <Radio value="2" size="lg">
                              <Text color={color}>24 meses</Text>
                            </Radio>
                            <Radio value="3" size="lg">
                              <Text color={color}>36 meses</Text>
                            </Radio>
                          </>
                        )}
                        {tipoCertificado === '6' && (
                          <>
                            <Radio value="3" size="lg">
                              <Text color={color}>
                                Trial (Validação Express)
                              </Text>
                            </Radio>
                            <Radio value="4" size="lg">
                              <Text color={color}>50 Transações</Text>
                            </Radio>
                            <Radio value="5" size="lg">
                              <Text color={color}>5000 Transações</Text>
                            </Radio>
                          </>
                        )}
                      </VStack>
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </PedidoBox>
          </SimpleGrid>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default Pedido;
